import React, { useState } from "react";
import { PieChart } from "lucide-react";

import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";
import { describeApiError } from "../../../lib/apiError";
import StoreShell from "../components/StoreShell";
import { fmtQty, monthRange, staffName } from "../utils/stores";
import { useStoreGetReportsQuery } from "../api/storesApi";

const naira = (n: number) =>
  `₦${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

/** One report table. Empty is said in words rather than left blank. */
function Panel({
  title, note, columns, rows, empty,
}: {
  title: string;
  note?: string;
  columns: string[];
  rows: (string | number)[][];
  empty: string;
}) {
  return (
    <div className="bg-white border rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b">
        <h2 className="font-bold text-gray-800">{title}</h2>
        {note && <p className="text-xs text-gray-400 mt-0.5">{note}</p>}
      </div>

      {rows.length === 0 ? (
        <p className="py-10 text-center text-xs text-gray-400 px-6">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                {columns.map((c, i) => (
                  <th key={c} className={`px-4 py-2 font-medium ${i > 0 ? "text-right" : ""}`}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50/60">
                  {r.map((cell, j) => (
                    <td
                      key={j}
                      className={`px-4 py-2.5 ${j === 0 ? "text-gray-900 font-medium" : "text-right text-gray-700"}`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * The four questions the secretariat actually asks of a store, answered over a
 * window — rather than a report builder nobody has time to drive.
 */
function StoreReportsInner() {
  const thisMonth = monthRange();
  const [from, setFrom] = useState(thisMonth.from);
  const [to, setTo] = useState(thisMonth.to);

  const { data, isLoading, error } = useStoreGetReportsQuery({ from, to });

  return (
    <StoreShell
      title="Reports"
      subtitle="What sold, who sold it, what came in and what each store is worth."
      actions={
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100" />
          </div>
        </div>
      }
    >
      {error && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm font-medium text-red-700">
          {describeApiError(error, "Could not build those reports.")}
        </div>
      )}

      {isLoading && <div className="py-14 text-center text-sm text-gray-400 animate-pulse">Working it out…</div>}

      {data && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Panel
            title="Sales by item"
            columns={["Item", "Units", "Total"]}
            rows={(data.sales_by_item ?? []).map((r: any) => [
              `${r.item?.name ?? "—"} (${r.item?.code ?? ""})`,
              fmtQty(r.units),
              naira(r.total),
            ])}
            empty="Nothing was sold in this period."
          />

          <Panel
            title="Sales by staff"
            note="Who sold it, from the stock they were holding."
            columns={["Staff", "Units", "Total"]}
            rows={(data.sales_by_seller ?? []).map((r: any) => [
              staffName(r.holder_admin ?? r.holderAdmin) || "—",
              fmtQty(r.units),
              naira(r.total),
            ])}
            empty="Nothing was sold in this period."
          />

          <Panel
            title="Receipts by supplier"
            columns={["Supplier", "Deliveries", "Units"]}
            rows={(data.receipts_by_supplier ?? []).map((r: any) => [
              r.supplier || "Not recorded",
              r.deliveries,
              fmtQty(r.units),
            ])}
            empty="Nothing was received in this period."
          />

          <Panel
            title="Stock value by store"
            note="As it stands now, not for the window above."
            columns={["Store", "Units", "Value"]}
            rows={(data.value_by_store ?? []).map((r: any) => [
              r.name,
              fmtQty(r.units),
              naira(r.value),
            ])}
            empty="No stores yet."
          />
        </div>
      )}

      {!isLoading && !data && !error && (
        <div className="bg-white border rounded-xl py-16 text-center">
          <PieChart className="w-9 h-9 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Nothing to report yet.</p>
        </div>
      )}
    </StoreShell>
  );
}

export default function StoreReportsPage() {
  return (
    <PageGate perm={PERMS.store.access}>
      <StoreReportsInner />
    </PageGate>
  );
}
